import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import { custom } from '../custom';
import { theme } from '../constants';
import { components } from '../components';
import { Eye, EyeOff } from 'lucide-react';

const SHOPIFY_STORES = {
    USA: {
        url: 'https://festivehq.myshopify.com/api/2025-07/graphql.json',
        token: '223e6f6958a6e1568db68d8891388212',
    },
    INDIA: {
        url: 'https://in.festivehq.com/api/2025-07/graphql.json', 
        token: '7061d5be89513b8d01e171d44f83584c', 
    },
};

const STORE_KEYS = ['USA', 'INDIA'] as const;
type StoreKey = typeof STORE_KEYS[number];

export const SignIn: React.FC = () => {
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);

    const cookieOptions = {
        expires: 7,
        path: '/',
        secure: typeof window !== 'undefined' && window.location?.protocol === 'https:',
        sameSite: 'Lax' as const,
    };

    useEffect(() => {
        const lsToken = localStorage.getItem('customerAccessToken');
        const cookieToken = Cookies.get('customerAccessToken');
        const token = lsToken || cookieToken;

        const isLoggedIn =
            localStorage.getItem('isLoggedIn') === 'true' || Cookies.get('isLoggedIn') === 'true';

        if (token && isLoggedIn) {
            navigate('/', { replace: true });
        }
    }, [navigate]);

    const isValidEmail = (email: string): boolean => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    // --- START OF UPDATED handleSignIn FUNCTION ---
    const handleSignIn = async () => {
        // 1. Initial Validation
        if (!email || !password || loading) {
            alert('Please enter both email and password.');
            return;
        }

        if (!isValidEmail(email)) {
            alert('Plea se enter a valid email address (e.g., example@gmail.com).');
            return;
        }

        const existingToken =
            localStorage.getItem('customerAccessToken') || Cookies.get('customerAccessToken');
        const existingLogin =
            localStorage.getItem('isLoggedIn') === 'true' || Cookies.get('isLoggedIn') === 'true';

        if (existingToken && existingLogin) {
            alert('You are already signed in!');
            navigate('/', { replace: true });
            return;
        }

        setLoading(true);

        // Ensure email and password are trimmed
        const trimmedEmail = email.trim();
        const trimmedPassword = password.trim();

        // GraphQL Mutation for Customer Access Token Creation
        const mutation = `
            mutation customerAccessTokenCreate($input: CustomerAccessTokenCreateInput!) {
                customerAccessTokenCreate(input: $input) {
                    customerAccessToken {
                        accessToken
                        expiresAt
                    }
                    customerUserErrors {
                        field
                        message
                    }
                }
            }
        `;
        const variables = { input: { email: trimmedEmail, password: trimmedPassword } };

        let success = false;
        let loggedInStoreKey: StoreKey | null = null;
        let accessToken: string | undefined = undefined;
        let lastErrorMessage = 'Login failed. Please verify your email and password.';

        // 2. Robust Multi-Store Iteration Attempt using indexed loop for 'isLastStore' check
        for (let i = 0; i < STORE_KEYS.length; i++) {
            const storeKey = STORE_KEYS[i];
            const isLastStore = i === STORE_KEYS.length - 1; // Correctly identify the final check
            const config = SHOPIFY_STORES[storeKey];

            try {
                const response = await fetch(config.url, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Shopify-Storefront-Access-Token': config.token,
                    },
                    body: JSON.stringify({ query: mutation, variables }),
                });

                const result = await response.json();
                const data = result.data?.customerAccessTokenCreate;
                
                accessToken = data?.customerAccessToken?.accessToken;
                const errorMessages = data?.customerUserErrors;
                
                // Success: Token found
                if (accessToken) {
                    success = true;
                    loggedInStoreKey = storeKey; 
                    break; // Found customer, stop iterating
                } 
                
                // Failure: Store error handling (User Errors)
                if (errorMessages?.length > 0) {
                    const errorMessage = errorMessages[0].message.toLowerCase();
                    lastErrorMessage = errorMessage; // Store the last user error
                    
                    // If it's NOT the last store, we continue to the next store check.
                    // If it is the last store, we break to display the final error.
                    if (!isLastStore) {
                        continue; 
                    }
                    
                    break; // Break on the last store error, as no more stores to check.
                }
                
                // Fallback: If data is null/undefined (e.g., a general GraphQL or network issue)
                if (!data) {
                    lastErrorMessage = `GraphQL request failed for ${storeKey}.`;
                    break;
                }

            } catch (err) {
                console.error(`Error during login check for ${storeKey}:`, err);
                lastErrorMessage = 'A network error occurred.';
                break; // ALWAYS break on network/critical error
            }
        }
        
        // 3. Handle Final Result (SUCCESS)
        if (success && accessToken && loggedInStoreKey) {
            
            // Save Session Tokens (Local Storage & Cookies)
            Cookies.set('customerAccessToken', accessToken, cookieOptions);
            Cookies.set('isLoggedIn', 'true', cookieOptions);
            Cookies.set('loggedInStoreKey', loggedInStoreKey, cookieOptions); 
            
            localStorage.setItem('customerAccessToken', accessToken);
            localStorage.setItem('isLoggedIn', 'true');
            localStorage.setItem('loggedInStoreKey', loggedInStoreKey);

            // Fetch Customer Details from the successful store
            const config = SHOPIFY_STORES[loggedInStoreKey]; 
            const customerQuery = `
                query getCustomer($customerAccessToken: String!) {
                    customer(customerAccessToken: $customerAccessToken) {
                        id
                        firstName
                        lastName
                        email
                        phone
                    }
                }
            `;

            try {
                const customerRes = await fetch(config.url, { 
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Shopify-Storefront-Access-Token': config.token, 
                    },
                    body: JSON.stringify({
                        query: customerQuery,
                        variables: { customerAccessToken: accessToken },
                    }),
                });

                const customerData = await customerRes.json();
                const customer = customerData.data.customer;

                if (customer?.id) {
                    const customerId = customer.id.split('/').pop();
                    Cookies.set('customerId', customerId); 
                    localStorage.setItem('shopifyUser', JSON.stringify(customer)); 
                }
                
                alert(`Signed in successfully `);
                navigate('/', { replace: true });
                
            } catch (fetchErr) {
                console.error('Customer Fetch Error:', fetchErr);
                alert(`Signed in to **${loggedInStoreKey}**, but failed to retrieve full customer details.`);
                navigate('/', { replace: true });
            }

        } else {
            // 4. Handle Final Result (FAILURE after checking both stores)
            
            if (lastErrorMessage.includes('unidentified customer') || lastErrorMessage.includes('unrecognized') || lastErrorMessage.includes('not found')) {
                alert('Invalid Email or no account found on either store. Please check the email.');
            } else if (lastErrorMessage.includes('incorrect') || lastErrorMessage.includes('invalid password')) {
                alert('Incorrect password. Please try again.');
            } else {
                alert(`Sign in failed: ${lastErrorMessage}`);
            }
        }
        
        setLoading(false);
    };
    // --- END OF UPDATED handleSignIn FUNCTION ---

    const renderHeader = () => <components.Header />;

    const renderContent = (): JSX.Element => (
        <div style={{ padding: '50px 20px 20px 20px' }}>
            <h1
                style={{
                    margin: 0,
                    padding: '0 16px',
                    textAlign: 'center',
                    fontFamily: theme.fonts.Mulish_700Bold.fontFamily,
                    fontWeight: 700,
                    color: theme.colors.mainColor,
                    fontSize: '2.5rem',
                    lineHeight: '1.3',
                    textTransform: 'capitalize',
                    marginBottom: '1rem',
                    letterSpacing: '0.5px',
                }}
            >
                Welcome back!
            </h1>

            <p
                style={{
                    margin: 0,
                    color: theme.colors.textColor,
                    fontSize: 16,
                    lineHeight: 1.6,
                    textAlign: 'center',
                    marginBottom: 40,
                    ...theme.fonts.Mulish_400Regular,
                }}
            >
                Sign in to continue
            </p>

            {/* Form */}
            <form
                onSubmit={(e) => {
                    e.preventDefault();
                    handleSignIn();
                }}
            >
                <custom.InputField
                    label="email"
                    containerStyle={{ marginBottom: 20 }}
                    placeholder="zenithsneaks@mail.com"
                    value={email}
                    autoComplete="email"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
                />

                <custom.InputField
                    clickable={true}
                    label="Password"
                    placeholder="••••••••"
                    type={showPassword ? 'text' : 'password'}
                    
                    icon={
                        <span
                            onClick={() => setShowPassword((prev) => !prev)}
                            style={{
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                            }}
                        >
                            {showPassword ? (
                                <Eye color={theme.colors.mainColor} />
                            ) : (
                                <EyeOff color={theme.colors.mainColor} />
                            )}
                        </span>
                    }
                    containerStyle={{ marginBottom: 20 }}
                    value={password}
                    autoComplete="current-password"
                    onChange={(e) => setPassword(e.target.value)}
                />
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        marginBottom: 30,
                    }}
                >
                    <button
                        onClick={() => navigate('/ForgotPassword')}
                        type="button"
                        style={{
                            color: theme.colors.mainColor,
                            fontSize: 16,
                            backgroundColor: 'transparent',
                            ...theme.fonts.Mulish_400Regular,
                        }}
                    >
                        Forgot password?
                    </button>
                </div>

                <components.Button
                    title={loading ? 'Signing In...' : 'Sign in'}
                    style={{ marginBottom: 20 }}
                    disabled={loading}
                    onClick={handleSignIn}
                />
            </form>

            {/*<div style={{ display: 'flex', alignItems: 'center', flexDirection: 'row' }}>
                <span
                    style={{
                        marginRight: 4,
                        ...theme.fonts.Mulish_400Regular,
                        fontSize: 16,
                        color: theme.colors.textColor,
                    }}
                >
                    Don’t have an account?
                </span>
                <span
                    style={{
                        ...theme.fonts.Mulish_400Regular,
                        fontSize: 16,
                        cursor: 'pointer',
                        color: theme.colors.mainColor,
                    }}
                    onClick={() => navigate('/SignUp')}
                >
                    Sign up.
                </span>
            </div>*/}

        </div>
    );

    return (
        <>
            {renderHeader()}
            {renderContent()}
            <components.BottomTabBar />
        </>
    );
};
